<template>
  <v-container fluid>
    <v-row class="flex-column mx-2">
      <v-col cols="12">
        <v-row>
          <v-col cols="12" class="reinstateTitle font-bold text-center mb-4">
            <h2>{{ $t('L_request_reinstatement_application') }}</h2>
          </v-col>
          <v-col cols="12" class="font-size--14 text-left mb-4">
            {{ $t('L_to_reinstate_your_coverage_we_will_mail_you_a_reinstatement_application') }}
          </v-col>
          <v-col cols="12" class="mb-4 font-bold">
            <h4>{{ $t('L_Follow_the_steps_below_to_complete_the_reinstatement_application') }}</h4>
          </v-col>
          <v-col cols="12" class="blueText font-size--14 font-bold text-left mb-4">
            <ul>
              <li>{{ $t('L_Provide_all_required_information') }}</li>
              <li>
                {{ $t('L_Each_adult_insured_will_need_to_answer_the_medical_history_questions') }}
              </li>
              <li>
                {{ $t('L_indicate_premium_payment_option_and_provide_required_payment_details') }}
              </li>
              <li>{{ $t('L_Each_adult_insured_should_sign_the_application') }}</li>
              <li>
                {{
                  $t('L_Mail_the_completed_application_to_the_mailing_address_on_the_application')
                }}
              </li>
            </ul>
          </v-col>
          <v-col cols="12" class="font-size--14 text-left mb-4">
            {{ $t('L_The_reinstatement_application_will_be_mailed_to_the_address_below') }}
          </v-col>
          <v-col cols="12" class="font-size--16 font-bold text-left mb-4">
            <h4>{{ $t('L_Your_Reinstatement_application_forms_will_be_mailed_to') }}</h4>
          </v-col>
          <v-col cols="12" class="font-size--14 font-bold text-left mb-2 d-flex">
            <h5>{{ $t('L_Your_Address') }}</h5>
            <span class="blueReinstatementText ml-4" @click="update = true">
              <v-icon color="#0C6DCA" :icon="mdiPencil" />
              {{ $t('L_Update') }}
            </span>
          </v-col>
          <v-col cols="12" v-if="update === false" class="font-size--16 font-weight-regular">
            <div>{{ streetaddress1 }}</div>
            <div>{{ streetaddress2 }}</div>
            <div>{{ city }}, {{ stateData }}</div>
            <div>{{ zip }}</div>
          </v-col>
          <v-col cols="12" sm="6" md="5" v-else>
            <v-form
              ref="submit_reinstatement"
              id="submit_reinstatement"
              v-model="validForm"
              autocomplete="off"
            >
              <OnexAddressLine1
                data-comp-id="update_contact_details_address_one"
                v-model="streetaddress1"
                :label="$t('L_Street_Address_-_Line_1')"
                :maxLength="50"
                required
              />
              <OnexAddressLine2
                data-comp-id="update_contact_details_address_two"
                v-model="streetaddress2"
                :label="$t('L_Street_Address_-_Line_2_optional')"
                :maxLength="50"
              />
              <OnexCity
                data-comp-id="update_contact_details_city"
                v-model="city"
                :label="$t('L_City')"
                :maxLength="50"
                required
              />
              <OnexState
                data-comp-id="update_contact_details_state"
                v-model="stateData"
                :country="currentPolicy.isCanadaPolicy ? 'CA' : 'US'"
                :label="currentPolicy.isCanadaPolicy ? $t('L_policy_province') : $t('L_State')"
              />
              <OnexZip
                id-prop="update_contact_details_zip_code"
                v-model="zip"
                :country="currentPolicy.isCanadaPolicy ? 'CA' : 'US'"
                :label="
                  currentPolicy.isCanadaPolicy ? $t('L_policy_postal_code') : $t('L_Zip_Code')
                "
              />
            </v-form>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <div class="font-size--16 mt-4">
              <p>
                <span>{{
                  $t(
                    'L_if_you_have_questions_please_contact_your_representative_or_client_service',
                    [customerServicePhoneNum]
                  )
                }}</span>
              </p>
            </div>
          </v-col>
        </v-row>

        <!-- Button -->
        <v-row class="d-flex align-center justify-center py-4">
          <v-col cols="12" sm="6" md="4" class="pr-4">
            <OnexButton
              :disabled="disableActions"
              block
              variant="outlined"
              color="primary"
              @click="cancelAction"
              :class="{ 'text-button-small': $vuetify.display.width <= 440 && locale === 'fr_CA' }"
            >
              {{ $t('L_cancel') }}
            </OnexButton>
          </v-col>
          <v-col cols="12" sm="6" md="4" class="pr-4">
            <OnexButton
              :disabled="disableActions || !validForm"
              block
              color="primary"
              @click="continueAction"
              :class="{ 'text-button-small': $vuetify.display.width <= 440 && locale === 'fr_CA' }"
            >
              {{ $t('L_Request_Application') }}
            </OnexButton>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { defineComponent, type ComponentOptions } from 'vue';
import { mdiPencil } from '@mdi/js';
import {
  OnexState,
  OnexAddressLine1,
  OnexAddressLine2,
  OnexCity,
  OnexZip,
  OnexButton,
} from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'ReinstatementNYStepThree',
  components: {
    OnexZip,
    OnexState,
    OnexAddressLine1,
    OnexAddressLine2,
    OnexCity,
    OnexButton,
  },
  props: {
    disableActions: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    update: false,
    streetaddress1: '',
    streetaddress2: '',
    city: '',
    stateData: '',
    zip: '',
    mdiPencil,
    validForm: true,
  }),
  computed: {
    ...mapState(usePolicyStore, {
      currentPolicy: 'currentPolicy',
    }),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    customerServicePhoneNum() {
      if (this.locale === 'en_US') {
        return '(800) 257-4725';
      } else if (this.locale === 'en_CA') {
        return '(800) 387-7876';
      } else if (this.locale === 'fr_CA') {
        return '(800) 265-4804';
      }
      return '(800) 257-4725';
    },
  },
  methods: {
    continueAction() {
      const userObj = {
        streetaddress1: this.streetaddress1,
        streetaddress2: this.streetaddress2,
        city: this.city,
        state: this.stateData,
        zip: this.zip,
      };
      this.$emit('continue-to-the-next-reinstatement-step', userObj);
    },
    cancelAction() {
      this.$emit('cancelClicked');
    },
  },
  mounted() {
    this.streetaddress1 = this.currentPolicy?.data?.ownerAddress.address1 || '';
    this.streetaddress2 = this.currentPolicy?.data?.ownerAddress.address2 || '';
    this.city = this.currentPolicy?.data?.ownerAddress.city || '';
    this.stateData = this.currentPolicy?.data?.ownerAddress.state || '';
    this.zip = this.currentPolicy?.data?.ownerAddress.zip || '';
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.reinstateTitle {
  font-size: 22px;
}
.blueReinstatementText {
  color: #0c6dca;
  cursor: pointer;
}
.blueText {
  color: #063562;
}
.text-button-small.v-btn--size-default {
  font-size: 0.875rem;
}
</style>
